/**
 * THE WEB SPEECH API, AS MUCH OF IT AS CAN BE TESTED WITHOUT A MICROPHONE.
 *
 * The constructor lookup and the transcript rule live here, pure; the hook that owns the
 * instance lives in `hooks/useSpeechRecognition.js`. The split is not tidiness — the
 * transcript rule is the part with a decision in it, and jsdom has no speech engine, so a
 * rule written inside the hook could only be tested through a fake of the thing under test.
 */

/**
 * The vendor-prefixed constructor, or NULL where there is none.
 *
 * <p>⚠ NULL IS A FIRST-CLASS ANSWER AND THE CALLER MUST HIDE THE CONTROL. Firefox ships no
 * SpeechRecognition at all, and Chrome exposes it only on a SECURE ORIGIN — so `https://…` and
 * `localhost` have it and a plain-http LAN address does not. A mic button that is present and
 * silently inert is worse than an absent one: there is nothing on screen to explain it and
 * nothing in the console either.
 *
 * <p>Standard name first. The prefixed one is the only implementation in Chrome today, but a
 * browser shipping both would have the unprefixed one be the current contract.
 */
export function speechRecognitionCtor(w = typeof window === 'undefined' ? undefined : window) {
  if (!w) return null;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/**
 * The transcript carried by one `result` event: everything settled, and everything still
 * provisional.
 *
 * <p>⚠ READS EVERY RESULT, NOT THE DELTA FROM `event.resultIndex`. The delta is the documented
 * idiom and it needs a mutable accumulator outside the handler — which is one more thing that
 * has to be cleared at exactly the right moment, and a missed clear means the next dictation
 * opens with the previous one's words. Reading the whole list is IDEMPOTENT: a re-delivered or
 * re-indexed event cannot duplicate a phrase, and there is no accumulator to reset. The cost is
 * re-reading a handful of entries per event.
 *
 * @returns `{ final, interim }` — never null, both possibly empty
 */
export function transcriptOf(event) {
  const results = event?.results;
  const n = Number(results?.length) || 0;
  let final = '';
  let interim = '';
  for (let i = 0; i < n; i += 1) {
    const result = results[i];
    const text = result?.[0]?.transcript;
    if (typeof text !== 'string') continue;
    if (result.isFinal) final += text;
    else interim += text;
  }
  return { final, interim };
}

/**
 * What the input should show while dictating: what was already typed, then what was heard.
 *
 * <p>⚠ THE TYPED TEXT IS A BASE, NOT SOMETHING TO OVERWRITE. Without one, an interim stream
 * replaces the whole box on every event, so a reader who typed half a query watches it vanish —
 * and the box flickers between partial phrasings of the entire line rather than of the phrase
 * being spoken. With one, only the spoken tail moves.
 *
 * <p>A separating space is added when the base needs one, so "show AAPL" + "with RSI" does not
 * become "show AAPLwith RSI". The spoken text is NOT trimmed at the end: the engine emits a
 * trailing space between phrases and removing it would join two dictated words.
 */
export function composeTranscript(base, heard) {
  const left = typeof base === 'string' ? base : '';
  const right = typeof heard === 'string' ? heard : '';
  if (!right) return left;
  if (!left) return right.replace(/^\s+/, '');
  return /\s$/.test(left) ? left + right : `${left} ${right.replace(/^\s+/, '')}`;
}

/**
 * A sentence for a `SpeechRecognitionErrorEvent.error` code.
 *
 * <p>The two permission codes are named specifically because they are the only ones a RETRY
 * cannot fix — the reader has to change a browser setting, and "the microphone could not be
 * used" sends them to press the button again instead. `no-speech` and `aborted` are not errors
 * a reader needs told about at all: the first is a quiet room and the second is them stopping.
 */
export function speechErrorMessage(code) {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access is blocked for this site — allow it in the browser’s address bar '
        + 'and press the mic again.';
    case 'audio-capture':
      return 'No microphone was found.';
    case 'network':
      return 'Speech recognition needs the network and could not reach it.';
    case 'no-speech':
    case 'aborted':
      return null;
    default:
      return code ? `The microphone stopped: ${code}.` : null;
  }
}
